import { CxpEsignSignerStatus, CxpTask } from '../../models';
import { EsignSignatureRequestRow } from '../cxp-esign-api.service';

/**
 * Expands each eSign row into one card per signature the caller owes on it.
 *
 * A bundle can address the same person twice — two signer slots sharing an email
 * address collapse onto one contact — and each slot is signed independently, so a
 * single card cannot say which one a click means. The signature-request read is the
 * only place that distinction exists, since the client-request shell the task list
 * is built from carries the bundle and not its recipients.
 *
 * Rows the read says nothing about are passed through untouched. That is the normal
 * case for a caller with one slot, and it is also what a failed read looks like — the
 * task list is worth more with un-expanded eSign cards than with none, and signing
 * still resolves a lone slot without being told which.
 */
export function expandEsignTasksBySigner(tasks: CxpTask[], signatureRequests: EsignSignatureRequestRow[]): CxpTask[] {
  const byClientRequestId = groupByClientRequestId(signatureRequests);

  return tasks.flatMap((task) => {
    if (task.type !== 'esign-client-request-list') return [task];
    const rows = byClientRequestId.get(task.sourceId);
    return rows?.length ? rows.map((row) => toSignerTask(task, row)) : [task];
  });
}

/**
 * Ordered by recipient id so the cards keep the same order across reloads. The read
 * makes no promise about order, and a list that reshuffles between two views reads as
 * the signatures themselves having changed.
 */
function groupByClientRequestId(rows: EsignSignatureRequestRow[]): Map<number, EsignSignatureRequestRow[]> {
  const grouped = new Map<number, EsignSignatureRequestRow[]>();

  rows
    .filter((row) => row.clientRequestId != null)
    .forEach((row) => {
      const key = row.clientRequestId as number;
      grouped.set(key, [...(grouped.get(key) ?? []), row]);
    });

  grouped.forEach((group, key) => grouped.set(key, [...group].sort((a, b) => a.recipientId - b.recipientId)));
  return grouped;
}

function toSignerTask(task: CxpTask, row: EsignSignatureRequestRow): CxpTask {
  const signerStatus = toSignerStatus(row);

  return {
    ...task,
    // The bundle id alone would collide across a contact's slots on one bundle, which
    // is the case this expansion exists for.
    key: `${task.type}-${task.sourceId}-${row.recipientId}`,
    esignRecipientId: row.recipientId,
    esignSignerStatus: signerStatus,
    esignSignerName: row.signerDisplayName,
    // Completion is this signature's, not the bundle's: a signer who has signed is
    // done whether or not their co-signers are.
    status: signerStatus === 'required' ? 'open' : 'completed',
    sourceStatus: row.status,
    dueDate: row.dueDate ?? task.dueDate,
  };
}

function toSignerStatus(row: EsignSignatureRequestRow): CxpEsignSignerStatus {
  if (!row.signed) return 'required';
  return row.awaitingOtherSigners ? 'awaiting-others' : 'signed';
}
